import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import {
  Paper,
  Stack,
  CircularProgress,
  Box,
  Card,
  CardContent,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
  Tooltip,
  Typography,
  GlobalStyles,
} from "@mui/material";
import moment from "moment";
import "moment/locale/ar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
// Sub-components
import MeetingDetailsPopover from "components/MeetingDetailsPopover";
import ReschedulePopover from "components/ReschedulePopover";
import ConfirmationDialog from "components/ConfirmationDialog";
import PropTypes from "prop-types";
import { updateMeetingComment } from "services/api";
import { updateMeetingDateTime } from "services/api";
import { deleteMeetingById } from "services/api";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
// 1. First import the library's CSS
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useTranslation } from "react-i18next";

// 2. Then import your custom CSS (this allows your CSS to override the library)
import "./index.css";
const InterviewCalendar = ({
  initialEvents,
  loading,
  currentDate,
  setCurrentDate,
  employeedLoggedId,
  formats,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.down("md")); // < 900px
  // --- Local Data State ---
  const [events, setEvents] = useState(initialEvents);
  const { t, i18n } = useTranslation();

  // Sync local events if initialEvents change from props
  useEffect(() => {
    setEvents(initialEvents);
  }, [initialEvents]);

  // --- UI States ---
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [rescheduleAnchorEl, setRescheduleAnchorEl] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // --- Form States ---
  const [comment, setComment] = useState("");
  const [dateVal, setDateVal] = useState("");
  const [timeVal, setTimeVal] = useState("");

  const localizer = momentLocalizer(moment);
  const isArabic = i18n.language === "ar";

  useEffect(() => {
    if (isArabic) {
      moment.locale("ar");
    } else {
      moment.locale("en");
    }
  }, [isArabic]);

  // --- Handlers: Popovers ---
  const handleEventClick = (event, e) => {
    setSelectedEvent(event);
    setComment(event.resource.comments || "");
    setAnchorEl(e.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  // --- Handlers: Reschedule (using your Virtual Anchor logic) ---
  const handleEditClick = (e, event) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();

    setRescheduleAnchorEl({
      getBoundingClientRect: () => rect,
      nodeType: 1,
    });

    setSelectedEvent(event);

    if (event?.start) {
      const d = new Date(event.start);
      setDateVal(moment(d).format("YYYY-MM-DD"));
      setTimeVal(moment(d).format("HH:mm"));
    }
  };

  const handleRescheduleClose = () => {
    setRescheduleAnchorEl(null);
  };

  // --- Logic: Save Comment ---
  const handleSaveComment = async () => {
    if (!selectedEvent) return;

    updateMeetingComment(selectedEvent.resource.id, comment);
    const result = "success"; // Placeholder for actual API call

    if (result !== "error") {
      setEvents((prev) =>
        prev.map((ev) =>
          ev.resource.id === selectedEvent.resource.id
            ? { ...ev, resource: { ...ev.resource, comments: comment } }
            : ev
        )
      );
      setSelectedEvent((prev) => ({
        ...prev,
        resource: { ...prev.resource, comments: comment },
      }));
      setAnchorEl(null);
    }
  };

  // --- Logic: Delete ---
  const handleDeleteIconClick = (e, meetingId) => {
    e.stopPropagation(); // Prevents opening the details popover
    setMeetingToDelete(meetingId);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!meetingToDelete) return;

    setDeleteLoading(true);

    try {
      const result = await deleteMeetingById(meetingToDelete);

      if (result !== "error") {
        //Update calendar ONLY on success
        setEvents((prevEvents) => prevEvents.filter((e) => e.resource.id !== meetingToDelete));
      }
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleteLoading(false);
      setConfirmOpen(false);
      setMeetingToDelete(null);
    }
  };

  const handleRescheduleSave = async () => {
    // 1. Validation
    if (!dateVal || !timeVal) {
      return;
    }

    try {
      // 2. Parse the native inputs (YYYY-MM-DD and HH:MM)
      const [year, month, day] = dateVal.split("-").map(Number);
      const [hours, minutes] = timeVal.split(":").map(Number);

      // 3. Create the initial Date object: (Month is 0-indexed: 0=Jan, 11=Dec)
      const dateTime = new Date(year, month - 1, day, hours, minutes, 0);

      // 4. "Midnight Fix" (Prevents All-Day flag on 00:00)
      if (hours === 0 && minutes === 0) {
        dateTime.setSeconds(1);
      }

      // --- TIMEZONE OFFSET LOGIC ---
      // Get offset in minutes
      const offset = dateTime.getTimezoneOffset();

      // Create new date adjusted by offset to preserve Wall-Clock time in ISO string
      const localDate = new Date(dateTime.getTime() - offset * 60 * 1000);

      // Slice to get "YYYY-MM-DDTHH:mm:ss" (removes Z and milliseconds)
      const formattedDateTime = localDate.toISOString().slice(0, 19);

      // 5. Identify the Meeting ID
      // Adjust this based on where your ID is stored (usually event.id or event.resource.id)
      const meetingId = selectedEvent?.resource?.id || selectedEvent?.id;

      // 6. Call your API wrapper
      const result = await updateMeetingDateTime(meetingId, formattedDateTime);

      if (result !== "error") {
        setEvents((prevEvents) =>
          prevEvents.map((evt) => {
            // Check if this is the event we modified
            if (evt.resource.id === meetingId) {
              return {
                ...evt,
                // Update visual position on calendar (requires Date object)
                start: dateTime,
                end: dateTime,
                // Update the underlying data (so popover shows new info next time)
                resource: {
                  ...evt.resource, // Merge backend result or manually update the date string
                  ...result,
                  meetingDate: formattedDateTime,
                },
              };
            }
            return evt; // Return other events unchanged
          })
        );

        // Also update selectedEvent so the details popover (if open) doesn't show stale data
        setSelectedEvent((prev) => ({
          ...prev,
          start: dateTime,
          resource: { ...prev.resource, meetingDate: formattedDateTime },
        }));
      } else {
        console.error("Failed to reschedule.");
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    }

    handleRescheduleClose();
  };

  const handleNavigate = (action) => {
    let newDate;
    if (action === "PREV") {
      newDate = moment(currentDate).subtract(1, "day").toDate();
    } else if (action === "NEXT") {
      newDate = moment(currentDate).add(1, "day").toDate();
    }
    setCurrentDate(newDate);
  };

  const handleDateChange = (e) => {
    const newDate = new Date(e.target.value);
    // Check if date is valid before setting to avoid crashes
    if (!isNaN(newDate.getTime())) {
      setCurrentDate(newDate);
    }
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <GlobalStyles
        styles={{
          ".rbc-timeslot-group": {
            minHeight: "100px !important", // Makes the hours taller
            borderBottom: "1px solid #f1f5f9 !important",
          },
          ".rbc-event": {
            minHeight: "85px !important", // Ensures the event box doesn't shrink
            backgroundColor: "transparent !important",
            border: "none !important",
          },
        }}
      />
      <Card
        sx={{
          borderRadius: 3,
          p: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <IconButton
              onClick={() => handleNavigate("PREV")}
              aria-label="previous day"
              sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}
            >
              <ChevronLeft />
            </IconButton>

            <TextField
              label={t("admin.talentAcquisition.meetings.selectDate")}
              type="date"
              size="small"
              // value={moment(currentDate).format("YYYY-MM-DD")}
              value={moment(currentDate).locale("en").format("YYYY-MM-DD")}
              onChange={handleDateChange}
              sx={{ width: 220 }}
              InputLabelProps={{
                shrink: true,
              }}
            />

            {/* NEXT ARROW */}
            <IconButton
              onClick={() => handleNavigate("NEXT")}
              aria-label="next day"
              sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}
            >
              <ChevronRight />
            </IconButton>
          </Stack>
          <Paper elevation={3} sx={{ p: 1 }}>
            {loading ? (
              <Stack alignItems="center" justifyContent="center" height={500}>
                <CircularProgress />
              </Stack>
            ) : (
              <Calendar
                localizer={localizer}
                culture={isArabic ? "ar" : "en"} // This tells the localizer to use 'ar'
                rtl={isArabic} // This flips the layout for Arabic
                events={events}
                toolbar={false}
                view="day"
                date={currentDate}
                onNavigate={(date) => setCurrentDate(date)}
                onSelectEvent={handleEventClick}
                startAccessor="start"
                endAccessor="end"
                style={{ height: "100%" }}
                formats={formats}
                dayLayoutAlgorithm="no-overlap"
                // 1. DYNAMIC STYLING BASED ON SCREEN SIZE
                eventPropGetter={(event) => {
                  const isOwner =
                    String(event.resource.interviewerId) === String(employeedLoggedId);

                  const borderColor = isOwner ? "#1976d2" : "#cbd5e1"; // blue for owner, gray otherwise
                  const backgroundColor = isOwner ? "#f0f7ff" : "#f8fafc";

                  return {
                    style: {
                      display: "flex",
                      alignItems: "center",
                      padding: "0 8px",
                      boxSizing: "border-box",

                      backgroundColor,
                      color: "#1e293b",

                      border: `1px solid ${borderColor}`, // base thin border
                      borderLeft: `5px solid ${borderColor}`, // left accent
                      borderRadius: "6px",

                      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                      cursor: "pointer",

                      transition: "all 0.2s ease",
                    },
                    className: "calendar-event", // optional: for extra styling in CSS if needed
                  };
                }}
                components={{
                  event: ({ event }) => {
                    const isOwner =
                      String(event.resource.interviewerId) === String(employeedLoggedId);

                    // Helper to truncate long names
                    const limit = (name, n) =>
                      name?.length > n ? name.substr(0, n - 1) + "…" : name;

                    const interviewerDisp = isOwner
                      ? t("admin.talentAcquisition.meetings.you")
                      : limit(event.resource.interviewerName || "Unknown", 10);
                    const applicantDisp = limit(event.resource.applicantName || "Unknown", 10);

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          p: 0.75,
                          overflow: "hidden",
                          justifyContent: "space-between",

                          // BORDER / ACCENT
                          backgroundColor: isOwner ? "#f0f7ff" : "#f8fafc",
                          border: `1px solid ${isOwner ? "#1976d2" : "#cbd5e1"}`,
                          borderLeft: `5px solid ${isOwner ? "#1976d2" : "#cbd5e1"}`,
                          borderRadius: "6px",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          boxSizing: "border-box", // ensures padding + border fit
                        }}
                      >
                        {/* TOP ROW: Rating */}
                        <Box>
                          <Typography
                            sx={{
                              display: "inline-block",
                              fontSize: "0.6rem",
                              fontWeight: 900,
                              color: isOwner ? "#1976d2" : "#475569",
                              bgcolor: isOwner ? "#dbeafe" : "#f1f5f9",
                              px: 0.6,
                              borderRadius: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            {t("admin.talentAcquisition.meetings.rating")}: {event.resource.rating}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          {/* LEFT: Interviewer → Applicant */}
                          <Typography
                            noWrap
                            sx={{
                              fontSize: isMobile ? "0.75rem" : "0.85rem",
                              fontWeight: 700,
                              color: "#1e293b",
                            }}
                          >
                            <span style={{ color: isOwner ? "#1976d2" : "inherit" }}>
                              {interviewerDisp}
                            </span>
                            <span style={{ margin: "0 4px", color: "#94a3b8", fontWeight: 400 }}>
                              {/* {"→"} */}
                              {isArabic ? (isOwner ? "←" : "→") : "→"}
                            </span>
                            {applicantDisp}
                          </Typography>

                          {/* RIGHT: Action Icons (Owner only) */}
                          {isOwner && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Tooltip title={t("common.edit")}>
                                <EditIcon
                                  sx={{
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    color: "#0056acff",
                                    "&:hover": { color: "#1e8bf8ff" },
                                  }}
                                  onClick={(e) => handleEditClick(e, event)}
                                />
                              </Tooltip>

                              <Tooltip title={t("common.cancel")}>
                                <IconButton
                                  onClick={(e) => handleDeleteIconClick(e, event.resource.id)}
                                  color="error"
                                  sx={{
                                    fontSize: "14px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  },
                }}
              />
            )}
          </Paper>
        </CardContent>
      </Card>
      <MeetingDetailsPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        selectedEvent={selectedEvent}
        employeedLoggedId={employeedLoggedId}
        comment={comment}
        setComment={setComment}
        handleSaveComment={handleSaveComment}
      />

      <ReschedulePopover
        open={Boolean(rescheduleAnchorEl)}
        anchorEl={rescheduleAnchorEl}
        onClose={handleRescheduleClose}
        dateVal={dateVal}
        setDateVal={setDateVal}
        timeVal={timeVal}
        setTimeVal={setTimeVal}
        handleRescheduleSave={handleRescheduleSave}
      />

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => !deleteLoading && setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        confirmDisabled={deleteLoading}
        loading={deleteLoading}
      />
    </Box>
  );
};

InterviewCalendar.propTypes = {
  initialEvents: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  localizer: PropTypes.object.isRequired,
  currentDate: PropTypes.instanceOf(Date).isRequired,
  setCurrentDate: PropTypes.func.isRequired,
  employeedLoggedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  formats: PropTypes.object,
};

export default InterviewCalendar;
